import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, message, Spin, ColorPicker, Input, Form, Card, Slider, Collapse } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import API from '../api/client';

const { Panel } = Collapse;

/* ── Custom drag hook (React 19 compatible, no findDOMNode) ── */
const useDrag = (position, onPositionChange, boundsRef) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const startMouse = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });

  const clamp = useCallback((pos) => {
    if (!boundsRef?.current || !ref.current) return pos;
    const parent = boundsRef.current.getBoundingClientRect();
    const el = ref.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(pos.x, parent.width - el.width)),
      y: Math.max(0, Math.min(pos.y, parent.height - el.height)),
    };
  }, [boundsRef]);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startMouse.current = { x: e.clientX, y: e.clientY };
    startPos.current = { ...position };

    const onMouseMove = (ev) => {
      if (!dragging.current) return;
      const dx = ev.clientX - startMouse.current.x;
      const dy = ev.clientY - startMouse.current.y;
      onPositionChange(clamp({ x: startPos.current.x + dx, y: startPos.current.y + dy }));
    };

    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [position, onPositionChange, clamp]);

  return { ref, onMouseDown };
};

const DragItem = ({ children, position, onStop, boundsRef }) => {
  const { ref, onMouseDown } = useDrag(position, onStop, boundsRef);

  return (
    <div
      ref={ref}
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        cursor: 'grab',
        zIndex: 5,
        display: 'inline-flex',
        justifyContent: 'center',
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  );
};

const QrGeneratorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const cardRef = useRef(null);

  const [business, setBusiness] = useState(null);
  const [logoSrc, setLogoSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const CARD_W = 340;
  const CARD_H = 480;
  const [bgColor, setBgColor] = useState('#a08c6e');
  const [textColor, setTextColor] = useState('#ffffff');

  const [logoSize, setLogoSize] = useState(72);
  const [logoPos, setLogoPos] = useState({ x: CARD_W / 2 - 36, y: 28 });

  const [nameSize, setNameSize] = useState(17);
  const [namePos, setNamePos] = useState({ x: 20, y: 112 });

  const [scanText, setScanText] = useState('OKUT');
  const [scanSize, setScanSize] = useState(11);
  const [scanPos, setScanPos] = useState({ x: CARD_W / 2 - 40, y: 175 });

  const [qrSize, setQrSize] = useState(160);
  const [qrPos, setQrPos] = useState({ x: CARD_W / 2 - 96, y: 210 });

  const [subtitle, setSubtitle] = useState('MENÜMÜZÜ GÖRÜNTÜLEMEK İÇİN OKUTUN');
  const [subSize, setSubSize] = useState(9);
  const [subPos, setSubPos] = useState({ x: 20, y: 420 });

  useEffect(() => {
    API.get('/businesses').then(async (res) => {
      const biz = res.data.find(b => b._id === id);
      if (biz) {
        setBusiness(biz);
        if (biz.image) {
          try {
            // Handle both absolute URLs and relative paths
            let imgPath = biz.image;
            try { imgPath = new URL(biz.image).pathname; } catch { /* already relative */ }
            const b64Res = await API.get(`/image-base64?url=${encodeURIComponent(imgPath)}`);
            if (b64Res.data?.dataUri) setLogoSrc(b64Res.data.dataUri);
          } catch (e) {
            console.warn('Logo yüklenemedi:', e);
          }
        }
      } else {
        message.error('İşletme bulunamadı');
        navigate('/admin/businesses');
      }
      setLoading(false);
    }).catch(() => {
      message.error('Veriler yüklenemedi');
      setLoading(false);
    });
  }, [id, navigate]);

  const menuUrl = business ? `${window.location.origin}/menu/${business.slug}` : '';

  const handleExportPDF = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdfW = 210;
      const pdfH = 297;
      const ratio = Math.min((pdfW * 0.65) / canvas.width, (pdfH * 0.65) / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      pdf.addImage(imgData, 'PNG', (pdfW - w) / 2, (pdfH - h) / 2, w, h);
      pdf.save(`${business.name}-menu-qr.pdf`);
      message.success('PDF indirildi!');
    } catch (err) {
      console.error(err);
      message.error('PDF oluşturulamadı');
    }
    setExporting(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!business) return null;

  const qrFg = bgColor;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/businesses')}>Geri</Button>
        <h2 style={{ margin: 0 }}>{business.name} - QR Kod Oluşturucu</h2>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Settings */}
        <Card style={{ width: 320, flexShrink: 0, maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
          <Collapse defaultActiveKey={['card']} ghost size="small">
            <Panel header="Kart" key="card">
              <Form layout="vertical" size="small">
                <Form.Item label="Arka Plan Rengi" style={{ marginBottom: 12 }}>
                  <ColorPicker value={bgColor} onChange={(_, hex) => setBgColor(hex)} showText />
                </Form.Item>
                <Form.Item label="Yazı Rengi" style={{ marginBottom: 12 }}>
                  <ColorPicker value={textColor} onChange={(_, hex) => setTextColor(hex)} showText />
                </Form.Item>
              </Form>
            </Panel>

            <Panel header="Logo" key="logo">
              <Form layout="vertical" size="small">
                <Form.Item label={`Boyut: ${logoSize}px`} style={{ marginBottom: 12 }}>
                  <Slider min={40} max={120} value={logoSize} onChange={(v) => {
                    const diff = (v - logoSize) / 2;
                    setLogoSize(v);
                    setLogoPos(p => ({ x: Math.max(0, p.x - diff), y: p.y }));
                  }} />
                </Form.Item>
              </Form>
            </Panel>

            <Panel header="İşletme Adı" key="name">
              <Form layout="vertical" size="small">
                <Form.Item label={`Yazı Boyutu: ${nameSize}px`} style={{ marginBottom: 12 }}>
                  <Slider min={10} max={28} value={nameSize} onChange={setNameSize} />
                </Form.Item>
              </Form>
            </Panel>

            <Panel header="Üst Yazı (Badge)" key="scan">
              <Form layout="vertical" size="small">
                <Form.Item label="Metin" style={{ marginBottom: 12 }}>
                  <Input value={scanText} onChange={e => setScanText(e.target.value)} />
                </Form.Item>
                <Form.Item label={`Yazı Boyutu: ${scanSize}px`} style={{ marginBottom: 12 }}>
                  <Slider min={8} max={18} value={scanSize} onChange={setScanSize} />
                </Form.Item>
              </Form>
            </Panel>

            <Panel header="QR Kod" key="qr">
              <Form layout="vertical" size="small">
                <Form.Item label={`Boyut: ${qrSize}px`} style={{ marginBottom: 12 }}>
                  <Slider min={100} max={240} value={qrSize} onChange={(v) => {
                    const diff = (v - qrSize) / 2;
                    setQrSize(v);
                    setQrPos(p => ({ x: Math.max(0, p.x - diff), y: p.y }));
                  }} />
                </Form.Item>
                <Form.Item label="Menü Linki" style={{ marginBottom: 0 }}>
                  <Input value={menuUrl} disabled size="small" />
                </Form.Item>
              </Form>
            </Panel>

            <Panel header="Alt Yazı" key="sub">
              <Form layout="vertical" size="small">
                <Form.Item label="Metin" style={{ marginBottom: 12 }}>
                  <Input value={subtitle} onChange={e => setSubtitle(e.target.value)} />
                </Form.Item>
                <Form.Item label={`Yazı Boyutu: ${subSize}px`} style={{ marginBottom: 12 }}>
                  <Slider min={7} max={16} value={subSize} onChange={setSubSize} />
                </Form.Item>
              </Form>
            </Panel>
          </Collapse>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            onClick={handleExportPDF}
            loading={exporting}
            block
            style={{ marginTop: 16 }}
          >
            PDF İndir
          </Button>

          <p style={{ fontSize: 11, color: '#999', marginTop: 12, textAlign: 'center' }}>
            Elemanları sürükleyerek konumlandırın
          </p>
        </Card>

        {/* Card Preview */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
          <div
            ref={cardRef}
            style={{
              width: CARD_W,
              height: CARD_H,
              background: bgColor,
              borderRadius: 20,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
              fontFamily: "'Poppins', 'Inter', Arial, sans-serif",
            }}
          >
            {/* Logo */}
            <DragItem position={logoPos} onStop={setLogoPos} boundsRef={cardRef}>
              <div style={{
                width: logoSize,
                height: logoSize,
                borderRadius: '50%',
                border: `2.5px solid ${textColor}`,
                overflow: 'hidden',
                background: 'rgba(255,255,255,0.15)',
                flexShrink: 0,
              }}>
                {logoSrc ? (
                  <img src={logoSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textColor, fontSize: 10, fontWeight: 600 }}>
                    LOGO
                  </div>
                )}
              </div>
            </DragItem>

            {/* Name */}
            <DragItem position={namePos} onStop={setNamePos} boundsRef={cardRef}>
              <div style={{
                color: textColor,
                fontSize: nameSize,
                fontWeight: 700,
                letterSpacing: 2.5,
                textTransform: 'uppercase',
                textAlign: 'center',
                lineHeight: 1.35,
                width: CARD_W - 40,
                userSelect: 'none',
              }}>
                {business.name}
              </div>
            </DragItem>

            {/* Scan Badge */}
            <DragItem position={scanPos} onStop={setScanPos} boundsRef={cardRef}>
              <div style={{
                border: `1.5px solid ${textColor}`,
                color: textColor,
                fontSize: scanSize,
                fontWeight: 700,
                letterSpacing: 3,
                padding: '5px 20px',
                textTransform: 'uppercase',
                userSelect: 'none',
              }}>
                {scanText}
              </div>
            </DragItem>

            {/* QR */}
            <DragItem position={qrPos} onStop={setQrPos} boundsRef={cardRef}>
              <div style={{ position: 'relative', padding: 10 }}>
                {[
                  { top: 0, left: 0, borderTop: `3px solid ${textColor}`, borderLeft: `3px solid ${textColor}` },
                  { top: 0, right: 0, borderTop: `3px solid ${textColor}`, borderRight: `3px solid ${textColor}` },
                  { bottom: 0, left: 0, borderBottom: `3px solid ${textColor}`, borderLeft: `3px solid ${textColor}` },
                  { bottom: 0, right: 0, borderBottom: `3px solid ${textColor}`, borderRight: `3px solid ${textColor}` },
                ].map((s, i) => (
                  <div key={i} style={{ position: 'absolute', width: 18, height: 18, ...s, zIndex: 2 }} />
                ))}
                <div style={{ background: '#fff', padding: 0, borderRadius: 2, lineHeight: 0 }}>
                  <QRCodeCanvas
                    value={menuUrl}
                    size={qrSize - 20}
                    bgColor="#ffffff"
                    fgColor={qrFg}
                    level="M"
                    includeMargin={false}
                  />
                </div>
              </div>
            </DragItem>

            {/* Subtitle */}
            <DragItem position={subPos} onStop={setSubPos} boundsRef={cardRef}>
              <div style={{
                color: textColor,
                fontSize: subSize,
                fontWeight: 600,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                textAlign: 'center',
                opacity: 0.9,
                lineHeight: 1.6,
                width: CARD_W - 40,
                userSelect: 'none',
              }}>
                {subtitle}
              </div>
            </DragItem>
          </div>
        </div>
      </div>
    </>
  );
};

export default QrGeneratorPage;
